// Entra login with MSAL (authorization code flow with PKCE, spec 4.1).
import { BrowserAuthError, InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from "@azure/msal-browser";
import type { ClientConfig } from "./api";

const RETURN_HASH_KEY = "pipeline.returnHash";
const RENEWED_KEY = "pipeline.renewedAt";
/**
 * The hidden frame of the silent renewal timed out or closed: third-party cookies blocked, or a second Microsoft
 * account signed in in the same browser. A full redirect to the login page renews the token instead.
 */
const SILENT_FAILURES = new Set(["monitor_window_timeout", "timed_out", "iframe_closed_prematurely"]);

export interface Auth {
  /** Account after login, or null while the browser is being sent to the Microsoft login page. */
  ensureLogin(): Promise<AccountInfo | null>;
  getToken(): Promise<string>;
  logout(): Promise<void>;
}

export async function createAuth(cfg: ClientConfig): Promise<Auth> {
  // Development only (stripped from the production build): a token from `az account get-access-token`, so that
  // screenshots of the running app work without an interactive login.
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_ACCESS_TOKEN) {
    const token = import.meta.env.VITE_DEV_ACCESS_TOKEN as string;
    return { ensureLogin: async () => ({}) as AccountInfo, getToken: async () => token, logout: async () => undefined };
  }
  const msal = new PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: `${location.origin}/`,
      postLogoutRedirectUri: `${location.origin}/`,
    },
    cache: { cacheLocation: "localStorage" },
  });
  await msal.initialize();
  const scopes = [cfg.apiScope];

  const redirectToLogin = async () => {
    sessionStorage.setItem(RETURN_HASH_KEY, location.hash);
    await msal.loginRedirect({ scopes });
  };

  return {
    async ensureLogin() {
      const result = await msal.handleRedirectPromise();
      const account = result?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
      if (!account) {
        await redirectToLogin();
        return null;
      }
      msal.setActiveAccount(account);
      const hash = sessionStorage.getItem(RETURN_HASH_KEY);
      if (result && hash) history.replaceState(null, "", `/${hash}`);
      sessionStorage.removeItem(RETURN_HASH_KEY);
      return account;
    },

    async getToken() {
      const account = msal.getActiveAccount();
      if (!account) {
        await redirectToLogin();
        throw new Error("Anmeldung erforderlich");
      }
      try {
        const token = (await msal.acquireTokenSilent({ scopes, account })).accessToken;
        sessionStorage.removeItem(RENEWED_KEY);
        return token;
      } catch (err) {
        const silentFailed = err instanceof BrowserAuthError && SILENT_FAILURES.has(err.errorCode);
        // Once per minute at most, so a renewal that keeps failing ends on the error page, not in a loop.
        const renewedRecently = Date.now() - Number(sessionStorage.getItem(RENEWED_KEY) ?? 0) < 60_000;
        // A redirect leaves the page: not while something typed is unsaved (a reply, a form). Then the request fails
        // with a clear hint, and the user signs in after saving.
        const unsaved = [...document.querySelectorAll<HTMLTextAreaElement | HTMLInputElement>("textarea, input[type=text], input:not([type])")].some(
          (x) => x.value !== x.defaultValue,
        );
        if ((err instanceof InteractionRequiredAuthError || silentFailed) && unsaved) {
          throw new Error("Die Anmeldung ist abgelaufen. Bitte Eingaben sichern (kopieren) und die Seite neu laden.", { cause: err });
        }
        if ((err instanceof InteractionRequiredAuthError || silentFailed) && !renewedRecently) {
          sessionStorage.setItem(RENEWED_KEY, String(Date.now()));
          sessionStorage.setItem(RETURN_HASH_KEY, location.hash);
          await msal.acquireTokenRedirect({ scopes, account });
        }
        throw err;
      }
    },

    async logout() {
      await msal.logoutRedirect({ account: msal.getActiveAccount() ?? undefined });
    },
  };
}
